import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { Sparkles, Truck, Heart, Youtube } from "lucide-react";
import { LOGO_URL, YOUTUBE_URL } from "../lib/brand";

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(console.error);
  }, []);

  const featured = products.slice(0, 6);

  return (
    <div data-testid="home-page">
      <Seo
        title="Home"
        path="/"
        description="Shop the official Anthony's Playworld merch — bright, playful tees, hoodies, hats, mugs, stickers, plushies and posters designed for kids and Playworld fans of every age. Free US shipping over $50."
      />
      {/* HERO */}
      <section className="pw-hero-bg border-b-4 border-black relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
         <div>
          <span className="pw-badge pink mb-6 inline-block" data-testid="hero-tag">
            OFFICIAL MERCH SHOP
          </span>
          <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl md:text-8xl">
            Wear the
            <br />
            <span className="bg-[#FFD500] border-4 border-black px-3 inline-block -rotate-1 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] mt-3">
              PLAYWORLD
            </span>
            <br />
            adventure!
          </h1>
          <p className="mt-8 text-lg sm:text-xl max-w-xl font-medium">
            Bright, bold, ridiculously fun merch straight from Anthony&apos;s
            Playworld. Tees, hoodies, plushies and stickers for big-kid energy.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/shop"
              className="pw-btn"
              data-testid="hero-shop-cta"
            >
              <Sparkles size={18} strokeWidth={3} className="mr-2" /> Shop Now
            </Link>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pw-btn pw-btn-ghost"
              data-testid="hero-youtube-cta"
            >
              <Youtube size={18} strokeWidth={3} className="mr-2" /> Watch on YouTube
            </a>
          </div>
         </div>

         <div className="relative flex justify-center items-center">
          <div className="relative">
            <img
              src={LOGO_URL}
              alt="Anthony's Playworld"
              className="pw-logo-img relative w-72 sm:w-96 h-72 sm:h-96 object-contain"
              data-testid="hero-logo"
            />
            <span
              className="pw-sticker"
              style={{ top: 10, right: -10, background: "#FF2E93", color: "#fff" }}
            >
              Zoom Zoom!
            </span>
            <span
              className="pw-sticker"
              style={{ bottom: 10, left: -10, background: "#00E5FF", transform: "rotate(6deg)" }}
            >
              New Drop
            </span>
          </div>
         </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="border-b-4 border-black bg-[#FFD500]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center font-bold uppercase tracking-wider">
          <FeatureItem icon={<Truck strokeWidth={3} />} text="Free shipping over $50" />
          <FeatureItem icon={<Heart strokeWidth={3} />} text="100% Kid-tested fun" />
          <FeatureItem icon={<Sparkles strokeWidth={3} />} text="New drops every month" />
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <span className="pw-badge yellow mb-3 inline-block">FRESH ARRIVALS</span>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">
              Featured Merch
            </h2>
          </div>
          <Link to="/shop" className="pw-btn pw-btn-sm pw-btn-blue" data-testid="view-all-cta">
            View All →
          </Link>
        </div>
        <p className="max-w-3xl text-lg leading-relaxed mb-10">
          These are the pieces our Playworld fans keep coming back for — a
          rotating mix of best-sellers, brand-new drops, and the loudest,
          brightest, most joyful merch we can dream up. Every item is designed,
          printed and shipped in small batches, so grab your favorites while
          they&apos;re in stock.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </section>

      {/* CATEGORY HIGHLIGHTS */}
      <section className="border-t-4 border-black bg-[#FFFDF0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <h2 className="font-display text-4xl sm:text-5xl uppercase mb-4">
            Shop by Vibe
          </h2>
          <p className="max-w-3xl text-lg mb-10 leading-relaxed">
            From huggable plushies to loud graphic tees, every drop from
            Anthony&apos;s Playworld is designed to make your day a little more
            colorful. Pick your category to explore the freshest arrivals in
            apparel, drinkware, stickers and more.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { label: "T-Shirts", color: "#FFD500" },
              { label: "Hoodies", color: "#FF2E93" },
              { label: "Mugs", color: "#00E5FF" },
              { label: "Stickers", color: "#39FF14" },
              { label: "Hats", color: "#FFD500" },
              { label: "Toys & Plushies", color: "#FF2E93" },
              { label: "Posters", color: "#00E5FF" },
            ].map((c) => (
              <Link
                key={c.label}
                to={`/shop?category=${encodeURIComponent(c.label)}`}
                className="pw-card p-6 text-center hover:rotate-1"
                style={{ background: c.color }}
                data-testid={`category-tile-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <p className="font-display text-xl uppercase">{c.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureItem({ icon, text }) {
  return (
    <div className="flex items-center justify-center gap-3">
      <span className="bg-white border-3 border-black rounded-full p-2" style={{ borderWidth: 3 }}>
        {icon}
      </span>
      <span>{text}</span>
    </div>
  );
}
